/**
 * LIFF 深連結進站時的「真正目的地」解析純函式。
 *
 * 背景（2026-09-03 dev log 實測）：已註冊使用者點 LIFF 深連結
 * `https://liff.line.me/<id>/register/step2/inviteCode?invite=803009`，
 * LINE 會先落在 endpoint 根路徑 `/`，把目的地放在
 * `?liff.state=%2Fregister%2Fstep2%2FinviteCode%3Finvite%3D803009`，
 * 要等 LIFF SDK 在 liff.init 過程中才把網址換成目的地。
 *
 * SDK 套用的時機不保證早於 LineLoginProvider 讀 window.location 做判斷——
 * 這是 race：有時套用得早（成功），有時套用得晚（此時 window.location.pathname
 * 還是 "/"，被誤判成「單純開 App」，深連結整個被吃掉、直接導去首頁）。同一連結
 * 因此時好時壞，非決定性 bug。
 *
 * 修法：不依賴 SDK 套用的時機，自己解析 `?liff.state=` 當唯一真相——
 * 有 liff.state 就用它解出的路徑＋query 當「有效位置」；沒有就照樣回傳當前
 * window.location（等於無異動），所有既有無深連結的行為完全不變。
 */

export interface EffectiveLocation {
  pathname: string;
  search: string;
}

/**
 * 模組載入當下（必早於任何 liff.init 呼叫）抄下的 liff.state 原始值。
 *
 * 追加背景（2026-09-03 dev log 第二輪實測）：LIFF SDK 在把人送達目的地「之前」
 * 會先把 liff.state 從網址移除——存在「已移除、還沒送達」的窗口。該窗口內
 * resolveEffectiveLocation 從當前 search 讀不到 liff.state，只剩 endpoint 自帶的
 * query（此案的 per-supplier LIFF endpoint 帶著 supplier/shopCode），深連結因此
 * 仍被誤判成「開 App」導首頁。
 *
 * SDK 是本 app 自己在 LineLoginProvider 呼叫的；本模組被 provider import，
 * module 評估必然早於 liff.init，所以這裡抄下的值保證是 LINE 落地時的原始值，
 * 不受 SDK 之後的移除影響。單頁應用整個生命週期只需要這一份（深連結只在
 * 進站當下有意義）。
 */
/** 開機當下的原始 location（診斷用：與 liff_boot_diag 事件一起送出，
 *  讓後台看得到「LINE 落地時網址長什麼樣」，不依賴前端 console）。 */
export const capturedBootLocation: EffectiveLocation = (() => {
  try {
    if (typeof window === "undefined") return { pathname: "", search: "" };
    return { pathname: window.location.pathname, search: window.location.search };
  } catch {
    return { pathname: "", search: "" };
  }
})();

/** 開機抄存的 sessionStorage 備援 key：liff.state 要活過「LINE 登入來回」（該來回會把
 *  網址上的 liff.state 洗掉，見 2026-09-03 診斷事件）。一次性：讀取即清。 */
const BOOT_LIFF_STATE_BACKUP_KEY = "boot_liff_state_backup";
/** 備援有效時間：只為活過「LINE 登入來回」（數秒～數十秒），給 10 分鐘綽綽有餘；
 *  過期即棄，避免舊深連結殘值在日後開 App 時劫持導頁。 */
const BACKUP_TTL_MS = 10 * 60 * 1000;

/** 2026-09-03 診斷實錘第二輪：LIFF SDK 的「自動登入」（liff.init 內部發起，非本 app 的
 *  liff.login 呼叫）回跳網址不含 liff.state，且 LINE webview 在登入來回會清 sessionStorage
 *  ——單靠 sessionStorage 的備援時好時壞。比照 InvitedCode 既有的「URL/sessionStorage/
 *  Cookie 三來源」經驗，備援寫三份（sessionStorage / localStorage / Cookie），讀到任一
 *  份即用，全部讀取即清（一次性）＋時效檢查。 */
const writeBackup = (raw: string): number => {
  const t = Date.now();
  const payload = JSON.stringify({ v: raw, t });
  try { window.sessionStorage.setItem(BOOT_LIFF_STATE_BACKUP_KEY, payload); } catch { /* 忽略 */ }
  try { window.localStorage.setItem(BOOT_LIFF_STATE_BACKUP_KEY, payload); } catch { /* 忽略 */ }
  try {
    document.cookie = `${BOOT_LIFF_STATE_BACKUP_KEY}=${encodeURIComponent(payload)}; path=/; max-age=600; SameSite=Lax`;
  } catch { /* 忽略 */ }
  lastNoteWrittenT = t;
  return t;
};

/** 旅程章（2026-09-09 dev 15:22 事故）：備援（紙條）本身不代表「正在繞登入」，
 *  單看紙條會有兩種誤判——(a) 上一趟旅程的殘值在這一趟的登入回跳被誤用（劫持）、
 *  (b) 為了防 (a) 的「開機清殘值」把進行中旅程的紙條誤殺（15:22 實錘：登入來回
 *  冒出的中繼視窗以光禿根路徑開機、觸發清除，早真正回跳一秒把備援燒掉）。
 *
 *  改成「章」語意：紙條照寫，但登入回跳**只認有章的紙條**。蓋章的時機只有兩個——
 *  liff.init 前（自動登入若把頁面抓走，撤章程式跑不到，章自然留下）、以及
 *  recoverLineSession 主動重登出發前。init 平安完成（沒被抓走）就撤章。
 *  章用 noteT 綁定特定紙條：只有「這一頁自己寫的紙條」蓋得了章，別頁的舊紙條
 *  永遠無章 → 劫持在邏輯上不可能，「開機清殘值」的清潔工因此整個拆除。 */
const JOURNEY_STAMP_KEY = "boot_liff_state_journey";

/** 本頁寫過的紙條時間戳（蓋章時綁定用）；null＝本頁沒寫過紙條。 */
let lastNoteWrittenT: number | null = null;
/** 本頁蓋過章才准撤章——中繼視窗（沒蓋章）不得誤撤別頁進行中旅程的章。 */
let stampedThisPage = false;

const writeStamp = (noteT: number): void => {
  const payload = JSON.stringify({ noteT });
  try { window.sessionStorage.setItem(JOURNEY_STAMP_KEY, payload); } catch { /* 忽略 */ }
  try { window.localStorage.setItem(JOURNEY_STAMP_KEY, payload); } catch { /* 忽略 */ }
  try {
    document.cookie = `${JOURNEY_STAMP_KEY}=${encodeURIComponent(payload)}; path=/; max-age=600; SameSite=Lax`;
  } catch { /* 忽略 */ }
  stampedThisPage = true;
};

const readAndClearStamp = (): number | null => {
  let payload: string | null = null;
  try {
    payload = window.sessionStorage.getItem(JOURNEY_STAMP_KEY);
    window.sessionStorage.removeItem(JOURNEY_STAMP_KEY);
  } catch { /* 忽略 */ }
  if (!payload) {
    try { payload = window.localStorage.getItem(JOURNEY_STAMP_KEY); } catch { /* 忽略 */ }
  }
  if (!payload) {
    try {
      const m = document.cookie.match(new RegExp(`(?:^|; )${JOURNEY_STAMP_KEY}=([^;]*)`));
      if (m?.[1]) payload = decodeURIComponent(m[1]);
    } catch { /* 忽略 */ }
  }
  clearStampEverywhere();
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload) as { noteT?: unknown };
    return typeof parsed.noteT === "number" ? parsed.noteT : null;
  } catch {
    return null;
  }
};

const clearStampEverywhere = (): void => {
  try { window.sessionStorage.removeItem(JOURNEY_STAMP_KEY); } catch { /* 忽略 */ }
  try { window.localStorage.removeItem(JOURNEY_STAMP_KEY); } catch { /* 忽略 */ }
  try {
    document.cookie = `${JOURNEY_STAMP_KEY}=; path=/; max-age=0; SameSite=Lax`;
  } catch { /* 忽略 */ }
};

/** liff.init 呼叫前蓋章：宣告「若接下來被抓去登入，這一頁的紙條就是回跳目的地」。
 *  本頁沒寫過紙條（單純開首頁等）就無章可蓋——上一趟的舊紙條因此永遠是無章的。 */
export const stampLoginJourney = (): void => {
  if (lastNoteWrittenT === null) return;
  writeStamp(lastNoteWrittenT);
};

/** liff.init 平安完成（沒被抓去登入、確定留在本頁）→ 撤章。
 *  只撤自己蓋的章：中繼視窗（stampedThisPage=false）呼叫等於 no-op。 */
export const revokeLoginJourney = (): void => {
  if (!stampedThisPage) return;
  stampedThisPage = false;
  clearStampEverywhere();
};

/** 主動觸發登入重定向（recoverLineSession 登出重登）前呼叫：把當下位置寫成紙條並蓋章，
 *  回跳開機憑章送達。 */
export const backupLocationForLoginRedirect = (pathname: string, search: string): void => {
  const t = writeBackup(encodeURIComponent(pathname + search));
  writeStamp(t);
};

const readAndClearBackup = (): string | null => {
  let payload: string | null = null;
  try {
    payload = window.sessionStorage.getItem(BOOT_LIFF_STATE_BACKUP_KEY);
    window.sessionStorage.removeItem(BOOT_LIFF_STATE_BACKUP_KEY);
  } catch { /* 忽略 */ }
  if (!payload) {
    try {
      payload = window.localStorage.getItem(BOOT_LIFF_STATE_BACKUP_KEY);
      window.localStorage.removeItem(BOOT_LIFF_STATE_BACKUP_KEY);
    } catch { /* 忽略 */ }
  }
  if (!payload) {
    try {
      const m = document.cookie.match(new RegExp(`(?:^|; )${BOOT_LIFF_STATE_BACKUP_KEY}=([^;]*)`));
      if (m?.[1]) payload = decodeURIComponent(m[1]);
    } catch { /* 忽略 */ }
  }
  // 清 cookie（無論讀到與否，一次性語意）
  try {
    document.cookie = `${BOOT_LIFF_STATE_BACKUP_KEY}=; path=/; max-age=0; SameSite=Lax`;
  } catch { /* 忽略 */ }
  try {
    window.localStorage.removeItem(BOOT_LIFF_STATE_BACKUP_KEY);
  } catch { /* 忽略 */ }
  // 章一律讀取即清（與紙條同壽命）；紙條沒章＝上一趟旅程的遺物，不可信。
  const stampNoteT = readAndClearStamp();
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload) as { v?: unknown; t?: unknown };
    if (typeof parsed.v !== "string" || typeof parsed.t !== "number") return null;
    if (Date.now() - parsed.t > BACKUP_TTL_MS) return null; // 過期殘值不劫持（最後保險）
    if (stampNoteT !== parsed.t) return null; // 無章或章對不上這張紙條 → 不讀（防劫持）
    return parsed.v;
  } catch {
    return null;
  }
};

/**
 * 這次開機是不是「LINE 登入回跳」（OAuth callback）？
 *
 * 2026-09-03 診斷第三輪實錘：登入 session 過期時，LINE 會從深連結**直接**跳去登入，
 * App 第一次開機就已經是回跳（網址只有 code/state/liffClientId，liff.state 從未出現
 * 在前端可見的網址上）——三層備援皆無從寫起。此時目的地只在 LIFF SDK 內部，SDK 會在
 * init 完成後才把網址換成目的地。判斷式給 provider 用：這種開機且抓不到目的地時，
 * 「等 SDK 換網址」而不是急著導首頁。
 */
export const looksLikeLiffOauthCallbackBoot = (search: string): boolean => {
  try {
    const p = new URLSearchParams(search);
    return !!(p.get("code") && p.get("state") && p.get("liffClientId"));
  } catch {
    return false;
  }
};

export const capturedLiffStateAtBoot: string | null = (() => {
  try {
    if (typeof window === "undefined") return null;
    const fromUrl = new URLSearchParams(window.location.search).get("liff.state");
    if (fromUrl) {
      // 寫入備援：接下來若繞 LINE 登入（自動或手動），回跳網址會被洗掉，
      // 登入回來的第二次開機從備援撈回。
      writeBackup(fromUrl);
      return fromUrl;
    }
    const path = window.location.pathname;
    if (path !== "/" && path !== "") {
      // 直連深連結 boot（2026-09-03 診斷第四輪）：網址本身就是目的地。LIFF SDK 對
      // 直連網址一樣可能在我們程式跑之前自動登入（回跳固定回根路徑、目的地被洗掉），
      // 但直連格式保證這裡「一定先跑到」——把目的地寫進三層備援，回跳時撈回。
      // 本次不需要 eff 覆蓋（location 已是目的地），回傳 null 走原 location。
      writeBackup(encodeURIComponent(path + window.location.search));
      return null;
    }
    // 根路徑開機：只有「登入回跳」（code/state/liffClientId 齊）才讀備援——
    // 一般開 App（直接點 OA 圖文選單主頁等）不讀，避免殘留備援劫持導頁。
    if (looksLikeLiffOauthCallbackBoot(window.location.search)) {
      return readAndClearBackup();
    }
    // 非回跳的根路徑開機：什麼都不做。舊版在這裡「順手清殘值」，但 2026-09-09 15:22
    // 實錘：登入來回會冒出光禿根路徑的中繼視窗，走到這裡把進行中旅程的備援清掉、
    // 早真正回跳一秒——清潔工殺了自己要保護的人。防劫持已由「旅程章」承擔
    //（無章紙條回跳不讀，見 JOURNEY_STAMP_KEY），殘值靠 TTL 自然過期即可。
    return null;
  } catch {
    return null;
  }
})();

/** 把 liff.state 的值解成路徑＋query；不是 / 開頭或解碼失敗回 null。 */
const parseLiffStateValue = (raw: string | null): EffectiveLocation | null => {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/")) {
      const qIdx = decoded.indexOf("?");
      return qIdx >= 0
        ? { pathname: decoded.slice(0, qIdx), search: decoded.slice(qIdx) }
        : { pathname: decoded, search: "" };
    }
  } catch {
    // 解碼失敗（例如壞的 percent-encoding）：不可信。
  }
  return null;
};

/**
 * 解析出 LIFF 深連結真正的目的地。
 *
 * - 沒有 `liff.state`：原樣回傳傳入的 pathname / search（等同未套用任何解析）。
 * - 有 `liff.state` 且解出的值是 `/` 開頭的路徑：拆成 pathname / search 回傳
 *   （search 含開頭的 `?`，與 window.location.search 格式一致）。
 * - `liff.state` 存在但格式不是 `/` 開頭、或解碼失敗：視為不可信，fallback 回
 *   傳入的原始值——避免把奇怪的值誤判成路徑而導去錯的地方。
 */
export const resolveEffectiveLocation = (
  pathname: string,
  search: string,
  /** 開機時抄下的 liff.state（見 capturedLiffStateAtBoot）——當前 search 已被 SDK
   *  清掉 liff.state 時的後備來源。呼叫端傳 capturedLiffStateAtBoot；測試可注入。 */
  fallbackRaw: string | null = null,
): EffectiveLocation => {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get("liff.state");
  } catch {
    raw = null;
  }
  const fromSearch = parseLiffStateValue(raw);
  if (fromSearch) return fromSearch;
  const fromFallback = parseLiffStateValue(fallbackRaw);
  if (fromFallback) return fromFallback;
  return { pathname, search };
};


